import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Mail, Phone, X, KeyRound, ShieldCheck, ArrowUpCircle, ArrowDownCircle, Trash2, Copy, Eye, EyeOff, Pencil, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { DESIGNATIONS, withCurrent } from "../lib/constants";

const EMPTY = { name: "", email: "", phone: "", password: "", designation: "" };

export default function Faculty() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetShown, setResetShown] = useState(null);
  const [showPw, setShowPw] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/faculty")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({ name: f.name || "", email: f.email, phone: f.phone || "", password: "", designation: f.designation || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { name, phone, designation } = form;
        await api.patch(`/faculty/${editing.id}`, { name, phone, designation });
        toast.success("Faculty updated");
      } else {
        await api.post("/faculty", form);
        toast.success("Faculty added");
      }
      setOpen(false);
      setForm(EMPTY);
      load();
    }
    catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const promote = async (f) => {
    if (!window.confirm(`Promote ${f.name} to Manager? The current manager will become Faculty.`)) return;
    try { await api.patch(`/users/${f.id}/role`, { role: "manager" }); toast.success("Promoted to Manager"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const demote = async (f) => {
    if (!window.confirm(`Demote ${f.name} to Faculty?`)) return;
    try { await api.patch(`/users/${f.id}/role`, { role: "faculty" }); toast.success("Demoted to Faculty"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.name}? This cannot be undone.`)) return;
    try { await api.delete(`/faculty/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const resetPassword = async () => {
    if (!resetPw || resetPw.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    try {
      const r = await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      setResetShown(r.data);
      setResetTarget(null);
      setResetPw("");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Access control</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Faculty</h1>
          <p className="text-brand-muted mt-1 text-sm">Manage faculty accounts. Only one Manager at a time.</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid="add-faculty-btn">
          <Plus className="h-4 w-4" /> Add Faculty
        </button>
      </div>

      {loading ? <div className="text-brand-muted">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => {
            const isManager = f.role === "manager";
            const isMe = f.id === user.id;
            return (
              <div key={f.id} className="bg-white border border-brand-border rounded-lg p-5" data-testid={`faculty-${f.email}`}>
                <div className="flex items-start gap-3">
                  <div className={`h-11 w-11 rounded-full grid place-items-center font-heading font-semibold ${isManager ? "bg-brand-primary text-white" : "bg-brand-primary/10 text-brand-primary"}`}>
                    {f.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-heading font-semibold truncate">{f.name}</div>
                      {isManager && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-primary text-white font-semibold inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Manager</span>}
                      {isMe && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-accent/15 text-[#9C5240] font-semibold">You</span>}
                    </div>
                    <div className="text-xs text-brand-muted truncate inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {f.email}</div>
                    {f.designation && <div className="text-xs text-brand-muted truncate inline-flex items-center gap-1 mt-0.5"><Briefcase className="h-3 w-3" /> {f.designation}</div>}
                    {f.phone && <div className="text-xs text-brand-muted truncate inline-flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {f.phone}</div>}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-brand-border flex flex-wrap gap-2">
                  <button onClick={() => openEdit(f)} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-xs font-medium" data-testid={`edit-faculty-${f.email}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => setResetTarget(f)} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-xs font-medium" data-testid={`reset-${f.email}`}>
                    <KeyRound className="h-3.5 w-3.5" /> Reset Password
                  </button>
                  {!isManager && !isMe && (
                    <button onClick={() => promote(f)} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-xs font-medium text-brand-primary" data-testid={`promote-${f.email}`}>
                      <ArrowUpCircle className="h-3.5 w-3.5" /> Promote
                    </button>
                  )}
                  {isManager && !isMe && (
                    <button onClick={() => demote(f)} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-xs font-medium" data-testid={`demote-${f.email}`}>
                      <ArrowDownCircle className="h-3.5 w-3.5" /> Demote
                    </button>
                  )}
                  {!isMe && (
                    <button onClick={() => remove(f)} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-xs font-medium text-brand-danger ml-auto" data-testid={`delete-faculty-${f.email}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Faculty modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6" data-testid="faculty-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold">{editing ? "Edit Faculty" : "Add Faculty"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <In label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="f-name" />
              <In label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="f-email" disabled={!!editing} />
              <In label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="f-phone" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Designation</label>
                <select
                  value={form.designation}
                  onChange={(e) => setForm({ ...form, designation: e.target.value })}
                  className="mt-1.5 w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none"
                  data-testid="f-designation"
                >
                  <option value="">Select designation…</option>
                  {withCurrent(DESIGNATIONS, form.designation).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {!editing && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Temporary Password</label>
                  <div className="relative mt-1.5">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      minLength={6}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full h-11 px-3 pr-12 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none"
                      data-testid="f-password"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50">
                      {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
              <button type="submit" disabled={saving} className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium disabled:opacity-60" data-testid="f-submit">{saving ? "Saving…" : editing ? "Update" : "Create"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setResetTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6">
            <h2 className="font-heading text-xl font-semibold">Reset password</h2>
            <p className="text-brand-muted text-sm mt-1">Set a new password for <b>{resetTarget.name}</b> ({resetTarget.email})</p>
            <div className="mt-4">
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  minLength={6}
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  placeholder="New password (min 6 chars)"
                  className="w-full h-11 px-3 pr-12 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none"
                  data-testid="reset-pw-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50">
                  {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
                </button>
              </div>
              <button onClick={() => setResetPw(genPw())} className="text-xs text-brand-primary font-medium mt-2" data-testid="gen-pw">Generate strong password</button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setResetTarget(null)} className="h-11 px-4 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt">Cancel</button>
              <button onClick={resetPassword} className="h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid="reset-confirm">Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset success modal showing credentials */}
      {resetShown && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setResetShown(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6" data-testid="reset-shown">
            <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand-primary" /> Password updated</h2>
            <p className="text-brand-muted text-sm mt-1">Share these credentials with the user. Shown only once.</p>
            <div className="mt-4 rounded-md border border-brand-border bg-brand-surfaceAlt p-4 text-sm font-mono">
              <div><b className="font-semibold">Name:</b> {resetShown.name}</div>
              <div><b className="font-semibold">Email:</b> {resetShown.email}</div>
              <div className="flex items-center gap-2 mt-1">
                <span><b className="font-semibold">Password:</b> {resetShown.new_password}</span>
                <button onClick={() => { navigator.clipboard.writeText(resetShown.new_password); toast.success("Copied"); }} className="inline-flex items-center gap-1 text-brand-primary text-xs font-medium"><Copy className="h-3 w-3" /> copy</button>
              </div>
            </div>
            <button onClick={() => setResetShown(null)} className="mt-4 w-full h-11 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function In({ label, value, onChange, type = "text", required, testId, disabled }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="mt-1.5 w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none disabled:opacity-60 disabled:cursor-not-allowed" data-testid={testId} />
    </div>
  );
}

function genPw() {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}
